'use client'

import { Suspense, lazy, useCallback, useEffect, useState } from 'react'
import { MdClose, MdDownload, MdOpenInNew } from 'react-icons/md'

import ConfirmModal from '@/components/confirm-modal'
import {
    type PreviewKind,
    resolvePreviewKind,
    resolveTextFlavor,
} from './file-kind'

export {
    canPreview,
    getExtension,
    resolvePreviewKind,
    resolveTextFlavor,
    type PreviewKind,
    type TextFlavor,
} from './file-kind'

/**
 * PDF e texto entram por `lazy`.
 *
 * O pdf.js sozinho passa de 1 MB, e o editor rico arrasta o TipTap inteiro.
 * Sem o corte, a tela de atendimento — que na maioria esmagadora das vezes só
 * abre uma foto — pagaria os dois no carregamento inicial.
 */
const PdfView = lazy(() => import('./pdf-view'))
const TextView = lazy(() => import('./text-view'))

export interface PreviewFile {
    /**
     * Ausente = ainda buscando a URL assinada.
     *
     * O visualizador precisa abrir no CLIQUE, não quando a resposta chega:
     * assinar a URL é uma ida ao servidor, e sem nada na tela nesse intervalo o
     * usuário conclui que não abriu e clica de novo.
     */
    url?: string
    /** Nome exibido no cabeçalho e usado para deduzir o tipo. */
    fileName?: string
    /** Preferido sobre a extensão na hora de decidir como exibir. */
    mimeType?: string | null
    /**
     * Força o modo de exibição. Só para quem já sabe o tipo por outro caminho —
     * sem isso, o tipo sai de `mimeType` + extensão.
     */
    kind?: PreviewKind
    /**
     * Habilita a edição de arquivos de texto. Ausente = somente leitura.
     * Recebe o conteúdo já no formato do arquivo.
     */
    onSaveText?: (content: string) => Promise<void>
}

interface Props {
    file: PreviewFile | null
    onClose: () => void
}

/**
 * Ícone puro, sem as classes `btn` do DaisyUI.
 *
 * O `btn` traz altura mínima, padding e fundo próprios do tema: o ícone acaba
 * deslocado dentro de um quadrado maior que ele. Sobre a mídia em tela cheia
 * não há o que destacar com fundo — o contraste vem do próprio fundo escuro.
 */
const ACTION_BUTTON_CLASS =
    'inline-flex items-center justify-center leading-none bg-transparent border-none p-0 text-white/70 hover:text-white transition-colors cursor-pointer'

/**
 * Visualizador único de arquivo, em tela cheia.
 *
 * Existe porque abrir o arquivo em nova aba tira o usuário do contexto: o
 * operador perde a conversa do atendimento, e quem está no Armazenamento perde
 * a pasta em que estava. Cada formato é atendido no mesmo lugar — imagem,
 * vídeo, áudio, PDF paginado e texto no editor do sistema. O que não se
 * encaixa em nenhum deles cai no download, que é o comportamento honesto.
 */
const FilePreviewModal = ({ file, onClose }: Props) => {
    const [dirty, setDirty] = useState(false)
    const [confirmingClose, setConfirmingClose] = useState(false)

    /** Fechar com edição pendente pergunta antes de descartar. */
    const requestClose = useCallback(() => {
        if (dirty) {
            setConfirmingClose(true)
            return
        }
        onClose()
    }, [dirty, onClose])

    // Esc fecha, e a página atrás não rola enquanto o visualizador está aberto.
    useEffect(() => {
        if (!file) return

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') requestClose()
        }
        window.addEventListener('keydown', onKeyDown)

        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        return () => {
            window.removeEventListener('keydown', onKeyDown)
            document.body.style.overflow = previousOverflow
        }
    }, [file, requestClose])

    // Trocar de arquivo zera o estado de edição do anterior.
    useEffect(() => {
        setDirty(false)
        setConfirmingClose(false)
    }, [file?.url])

    if (!file) return null

    const kind = file.kind ?? resolvePreviewKind(file.fileName, file.mimeType)
    const renderContent = () => {
        // Enquanto a URL não chega, o modal já está na tela com o nome do
        // arquivo — o usuário vê que o clique foi atendido.
        if (!file.url) {
            return (
                <div className="flex flex-col items-center gap-3">
                    <span className="loading loading-spinner loading-lg text-white/70" />
                    <p className="text-sm text-white/60">Abrindo…</p>
                </div>
            )
        }

        switch (kind) {
            case 'image':
                return (
                    <img
                        src={file.url}
                        alt={file.fileName ?? 'Imagem'}
                        className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                        onClick={event => event.stopPropagation()}
                    />
                )

            case 'video':
                return (
                    <video
                        src={file.url}
                        controls
                        autoPlay
                        className="max-w-full max-h-full rounded-lg shadow-2xl"
                        onClick={event => event.stopPropagation()}
                    />
                )

            case 'audio':
                return (
                    <audio
                        src={file.url}
                        controls
                        autoPlay
                        className="w-full max-w-xl"
                        onClick={event => event.stopPropagation()}
                    />
                )

            case 'pdf':
                return <PdfView url={file.url} fileName={file.fileName} />

            case 'text':
                return (
                    <TextView
                        url={file.url}
                        flavor={resolveTextFlavor(file.fileName, file.mimeType)}
                        onSave={file.onSaveText}
                        onDirtyChange={setDirty}
                    />
                )

            default:
                return (
                    <div
                        className="flex flex-col items-center gap-4 text-center px-6"
                        onClick={event => event.stopPropagation()}
                    >
                        <p className="text-white/90">
                            Este tipo de arquivo não tem visualização
                        </p>
                        <p className="text-white/50 text-sm max-w-md">
                            Baixe para abrir no programa do seu computador.
                        </p>
                        <a href={file.url} download className="btn btn-primary btn-sm">
                            <MdDownload />
                            Baixar
                        </a>
                    </div>
                )
        }
    }

    return (
        <>
            <div
                className="fixed inset-0 z-[80] bg-black/85 flex flex-col"
                onClick={requestClose}
                role="dialog"
                aria-modal="true"
            >
                <div
                    className="flex items-center justify-between gap-3 px-4 py-3 text-white/90 shrink-0"
                    onClick={event => event.stopPropagation()}
                >
                    <span className="text-sm truncate min-w-0">
                        {file.fileName ?? ''}
                    </span>
                    <div className="flex items-center gap-4 shrink-0">
                        {/* Sem URL não há o que abrir nem baixar: link vazio
                            levaria a página para lugar nenhum. */}
                        {file.url && (
                            <>
                                <a
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={ACTION_BUTTON_CLASS}
                                    title="Abrir em nova aba"
                                >
                                    <MdOpenInNew size={20} />
                                </a>
                                <a
                                    href={file.url}
                                    download
                                    className={ACTION_BUTTON_CLASS}
                                    title="Baixar"
                                >
                                    <MdDownload size={20} />
                                </a>
                            </>
                        )}
                        <button
                            type="button"
                            className={ACTION_BUTTON_CLASS}
                            onClick={requestClose}
                            title="Fechar (Esc)"
                            aria-label="Fechar"
                        >
                            <MdClose size={24} className="text-white" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 min-h-0 flex items-center justify-center p-4 pt-0">
                    <Suspense
                        fallback={
                            <span className="loading loading-spinner loading-lg text-white/70" />
                        }
                    >
                        {renderContent()}
                    </Suspense>
                </div>
            </div>

            <ConfirmModal
                isOpen={confirmingClose}
                onClose={() => setConfirmingClose(false)}
                onConfirm={() => {
                    setConfirmingClose(false)
                    onClose()
                }}
                title="Descartar alterações?"
                message="Você editou o conteúdo e ainda não salvou. Fechar agora perde o que foi escrito."
                confirmButtonText="Descartar"
                cancelButtonText="Continuar editando"
                confirmButtonClass="btn-error"
            />
        </>
    )
}

export default FilePreviewModal
